import { useState } from 'react';
import { Plus, Trash2, Check, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import {
  useTaskTags,
  useCreateTaskTag,
  useUpdateTaskTag,
  useDeleteTaskTag,
} from '../../hooks/queries/useTaskTags';
import { cn } from '../../lib/cn';

// Palette partagée avec le picker de tags (cf. cabinet_tags / CabinetTagsManagerModal).
export const PALETTE = [
  '#64748b',
  '#0891b2',
  '#0d9488',
  '#16a34a',
  '#ca8a04',
  '#dc2626',
  '#9333ea',
  '#db2777',
];

function ColorPalette({
  value,
  onChange,
}: {
  value: string;
  onChange: (c: string) => void;
}) {
  return (
    <div className="flex gap-1">
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={cn(
            'w-6 h-6 rounded-full border-2 transition',
            value === c ? 'border-neutral-900' : 'border-transparent hover:border-neutral-300',
          )}
          style={{ backgroundColor: c }}
          aria-label={`Couleur ${c}`}
        />
      ))}
    </div>
  );
}

export function TagsTab() {
  const { data: tags = [], isLoading, error } = useTaskTags();
  const create = useCreateTaskTag();
  const update = useUpdateTaskTag();
  const remove = useDeleteTaskTag();

  const [name, setName] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [color, setColor] = useState<string>(PALETTE[0]!);

  // Édition inline d'un tag existant.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string>(PALETTE[0]!);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await create.mutateAsync({ name: trimmed, color });
      setName('');
      setColor(PALETTE[0]!);
      toast.success('Étiquette créée');
    } catch {
      toast.error('Impossible de créer l’étiquette (nom déjà utilisé ?)');
    }
  }

  function startEdit(id: string, currentName: string, currentColor: string) {
    setEditingId(id);
    setEditName(currentName);
    setEditColor(currentColor);
  }

  async function saveEdit() {
    if (!editingId) return;
    const trimmed = editName.trim();
    if (!trimmed) return;
    try {
      await update.mutateAsync({ id: editingId, patch: { name: trimmed, color: editColor } });
      setEditingId(null);
      toast.success('Étiquette mise à jour');
    } catch {
      toast.error('Impossible de modifier l’étiquette');
    }
  }

  async function handleDelete(id: string, tagName: string) {
    if (!window.confirm(`Supprimer l’étiquette « ${tagName} » ? Elle sera retirée de toutes les tâches.`)) {
      return;
    }
    try {
      await remove.mutateAsync(id);
      toast.success('Étiquette supprimée');
    } catch {
      toast.error('Suppression réservée aux administrateurs');
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-cime-text-primary">Étiquettes des tâches</h2>
        <p className="text-sm text-cime-text-muted mt-1">
          Les étiquettes (avec code couleur) sont partagées par tout le cabinet et peuvent être
          appliqués aux tâches.
        </p>
      </div>

      {/* Création : le formulaire se déplie au clic sur « Nouvelle étiquette » */}
      {formOpen ? (
        <form
          onSubmit={handleCreate}
          className="flex flex-col gap-3 p-4 border border-neutral-200 rounded-cime bg-neutral-50"
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom de la nouvelle étiquette"
            maxLength={50}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <ColorPalette value={color} onChange={setColor} />
            <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
              <Plus className="w-3.5 h-3.5" /> Créer le tag
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={() => setFormOpen(true)}>
            <Plus className="w-3.5 h-3.5" /> Nouvelle étiquette
          </Button>
        </div>
      )}

      {/* Liste */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Erreur de chargement des tags.</p>
      ) : tags.length === 0 ? (
        <p className="text-sm text-cime-text-muted italic">Aucun tag défini.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-cime">
          {tags.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-4 py-3">
              {editingId === t.id ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={50}
                    className="flex-1"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                  <ColorPalette value={editColor} onChange={setEditColor} />
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={!editName.trim() || update.isPending}
                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                    title="Enregistrer"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="p-1.5 text-neutral-400 hover:bg-neutral-100 rounded"
                    title="Annuler"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <span
                    className="inline-flex items-center text-xs px-2 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: t.color }}
                  >
                    {t.name}
                  </span>
                  <span className="flex-1" />
                  <button
                    type="button"
                    onClick={() => startEdit(t.id, t.name, t.color)}
                    className="p-1.5 text-neutral-400 hover:text-cime-primary hover:bg-neutral-100 rounded"
                    title="Modifier"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id, t.name)}
                    className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
